import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { LscVodCandidate } from '../types';
import { createLscEmptyHudHandoffAttestation } from '../api';
import { useLeague } from '../hooks/useLeague';
import LscMutationErrorAlert from './LscMutationErrorAlert';
import { parseLscMutationError, type ParsedLscMutationError } from '../entitlementBillingErrors';
import {
  truthActiveEmptyHudHandoffAttestations,
  truthEmptyHudHandoffAttestationSummary,
  truthIsEmptyHudHandoffAttestationEligible,
} from '../vodTruth';
import {
  CONFIRM_RIGHT_GAME_LABEL,
  CONFIRM_RIGHT_GAME_MODAL_TITLE,
  HANDOFF_ATTESTATION_EVIDENCE_INTRO,
  HANDOFF_ATTESTATION_HELPER,
  HANDOFF_ATTESTATION_TITLE,
} from '../commissionerReviewCopy';

interface CommissionerEmptyHudHandoffAttestationPanelProps {
  leagueId: string;
  candidate: LscVodCandidate;
  onUpdated: () => Promise<void>;
}

const CommissionerEmptyHudHandoffAttestationPanel: React.FC<
  CommissionerEmptyHudHandoffAttestationPanelProps
> = ({ leagueId, candidate, onUpdated }) => {
  const { isCommissioner } = useLeague();
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ParsedLscMutationError | null>(null);

  const eligible = truthIsEmptyHudHandoffAttestationEligible(candidate);
  const activeAttestations = truthActiveEmptyHudHandoffAttestations(candidate);
  const summary = truthEmptyHudHandoffAttestationSummary(candidate);

  if (!isCommissioner) {
    return null;
  }

  if (!eligible && activeAttestations.length === 0) {
    return null;
  }

  const submitAttestation = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await createLscEmptyHudHandoffAttestation(leagueId, candidate.id, {
        note:
          note.trim() || HANDOFF_ATTESTATION_EVIDENCE_INTRO,
      });
      setConfirmOpen(false);
      setNote('');
      await onUpdated();
    } catch (err) {
      setError(parseLscMutationError(err, 'Handoff attestation failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="sm:col-span-2 rounded-lg border border-sky-800/60 bg-sky-950/20 p-4 space-y-4"
      id="empty-hud-handoff-attestation"
      data-testid="commissioner-empty-hud-handoff-attestation-panel"
    >
      <div>
        <h3 className="text-sm font-semibold text-sky-100">{HANDOFF_ATTESTATION_TITLE}</h3>
        <p className="text-sm text-sky-200/80 mt-1">{HANDOFF_ATTESTATION_HELPER}</p>
      </div>

      <LscMutationErrorAlert error={error} onDismiss={() => setError(null)} />

      {summary && (
        <div
          className="rounded border border-green-800/60 bg-green-950/30 p-3 text-sm text-green-100 whitespace-pre-line"
          data-testid="empty-hud-handoff-attestation-evidence"
        >
          {summary}
        </div>
      )}

      {eligible && (
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-secondary-400">Note (optional)</span>
            <textarea
              className="input-field min-h-[72px]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Commissioner verified this VOD belongs to the verified Browns @ Lions handoff match."
              data-testid="empty-hud-handoff-attestation-note"
            />
          </label>

          <button
            type="button"
            className="btn-primary"
            disabled={submitting}
            onClick={() => setConfirmOpen(true)}
            data-testid="empty-hud-handoff-attestation-submit-button"
          >
            {CONFIRM_RIGHT_GAME_LABEL}
          </button>
        </div>
      )}

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="empty-hud-handoff-attestation-modal-title"
          data-testid="empty-hud-handoff-attestation-confirm-modal"
        >
          <div className="card max-w-md w-full relative">
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="absolute top-4 right-4 text-secondary-400 hover:text-white"
              aria-label="Close"
              disabled={submitting}
            >
              <X className="h-5 w-5" />
            </button>
            <h2
              id="empty-hud-handoff-attestation-modal-title"
              className="text-lg font-semibold text-white mb-4"
            >
              {CONFIRM_RIGHT_GAME_MODAL_TITLE}
            </h2>
            <p className="text-sm text-secondary-300 mb-6" data-testid="confirm-attestation-copy">
              This confirms the VOD belongs to the verified handoff match for this league.
              It does not verify scoreboard gamertags and does not auto-publish to the archive.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void submitAttestation()}
                disabled={submitting}
                data-testid="confirm-empty-hud-handoff-attestation-button"
              >
                {submitting ? 'Saving…' : CONFIRM_RIGHT_GAME_LABEL}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionerEmptyHudHandoffAttestationPanel;
